import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { getGuestInviteReport } from "../../server/Api";
import {
  ReportPageShell,
  exportRowsToExcel,
  formatDate,
  safeFileLabel,
} from "./reportShared";
import "../../assets/css/occasion.css";
import "../../assets/css/StayReports.css";
import "../../assets/css/FestivaReports.css";

const KIND_FILTERS = [
  { id: "all", label: "All invite types" },
  { id: "invitation", label: "Invites" },
  { id: "accommodation", label: "Accommodation" },
  { id: "travel", label: "Travel" },
  { id: "seat", label: "Seating" },
  { id: "itinerary", label: "Itinerary" },
];

const formatChannels = (channels = []) =>
  channels.filter(Boolean).join(" + ") || "Sent";

const formatSendDateTime = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatGroupItem = (item, kind, eventLevel) => {
  const channel = item.channel || formatChannels(item.channels);
  const when = formatSendDateTime(item.sendDate);
  if (eventLevel) {
    return [channel, when].filter(Boolean).join(" · ");
  }
  return [item.occasionName || "Occasion", channel, when]
    .filter(Boolean)
    .join(" · ");
};

const uniqueChannels = (group) => {
  const names = (group?.items || [])
    .map((item) => item.channel || (item.channels || [])[0])
    .filter(Boolean);
  return [...new Set(names)];
};

const communicationBlocks = (group) => {
  const items = group.items || [];
  if (group.eventLevel) {
    return [{ occasionName: "", lines: items }];
  }
  const blocks = [];
  items.forEach((item) => {
    const occasionName = item.occasionName || "Occasion";
    const last = blocks[blocks.length - 1];
    if (last && last.occasionName === occasionName) {
      last.lines.push(item);
    } else {
      blocks.push({ occasionName, lines: [item] });
    }
  });
  return blocks;
};

const CommunicationsCell = ({ groups = [], rowKey }) => {
  const [openKind, setOpenKind] = useState("");
  const [popoverPos, setPopoverPos] = useState(null);
  const chipRefs = useRef({});
  const panelRef = useRef(null);

  useEffect(() => {
    if (!openKind) return undefined;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type === "mousedown") {
        const chip = chipRefs.current[openKind];
        if (panelRef.current?.contains(event.target) || chip?.contains(event.target)) {
          return;
        }
      }
      setOpenKind("");
      setPopoverPos(null);
    };
    const reposition = () => {
      const chip = chipRefs.current[openKind];
      if (!chip) return;
      const rect = chip.getBoundingClientRect();
      const width = 280;
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
      const top = rect.bottom + 8;
      setPopoverPos({ top, left, width });
    };
    reposition();
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [openKind]);

  if (!groups.length) {
    return <span className="festiva-report-muted">No invites sent</span>;
  }

  const openGroup = groups.find((group) => group.kind === openKind);

  return (
    <div className="festiva-comm">
      <div className="festiva-comm-toggles">
        {groups.map((group) => {
          const isOpen = openKind === group.kind;
          const channels = uniqueChannels(group);
          return (
            <button
              key={`${rowKey}-${group.kind}`}
              type="button"
              ref={(node) => {
                chipRefs.current[group.kind] = node;
              }}
              className={`festiva-comm-chip${isOpen ? " is-open" : ""}`}
              aria-expanded={isOpen}
              title="View send details"
              onClick={() => {
                setOpenKind(isOpen ? "" : group.kind);
                setPopoverPos(null);
              }}
            >
              <span className="festiva-comm-chip-title">{group.kindLabel}</span>
              <span className="festiva-comm-chip-channels">
                {channels.length
                  ? channels.map((channel) => (
                      <span key={`${rowKey}-${group.kind}-${channel}`}>
                        {channel}
                      </span>
                    ))
                  : <span>Sent</span>}
              </span>
            </button>
          );
        })}
      </div>
      {openGroup && popoverPos && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={panelRef}
              className="festiva-comm-popover"
              style={{
                top: popoverPos.top,
                left: popoverPos.left,
                width: popoverPos.width,
              }}
            >
              <div className="festiva-comm-popover-title">
                {openGroup.kindLabel}
              </div>
              {communicationBlocks(openGroup).map((block, blockIdx) => (
                <div
                  key={`${openGroup.kind}-${block.occasionName || "all"}-${blockIdx}`}
                  className="festiva-comm-occasion"
                >
                  {block.occasionName ? (
                    <div className="festiva-comm-occ">{block.occasionName}</div>
                  ) : null}
                  <div className="festiva-comm-rows">
                    {block.lines.map((item, idx) => (
                      <div
                        key={`${openGroup.kind}-${idx}`}
                        className="festiva-comm-row"
                      >
                        <span className="festiva-comm-channel">
                          {item.channel || (item.channels || [])[0] || "Sent"}
                        </span>
                        <span className="festiva-comm-time">
                          {item.sendDate
                            ? formatSendDateTime(item.sendDate)
                            : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

const rsvpRowsForPerson = (row) => {
  const details = Array.isArray(row.rsvpDetails) ? row.rsvpDetails : [];
  if (details.length) {
    return details.map((item) => ({
      occasionId: item.occasionId,
      occasionName: String(item.occasionName || "Occasion").trim() || "Occasion",
      response: item.response || "Pending",
    }));
  }
  // Fallback for older payloads without per-occasion RSVP rows.
  return [
    ...new Set(
      (row.allocatedOccasions || [])
        .map((name) => String(name || "").trim())
        .filter(Boolean)
    ),
  ].map((occasionName) => ({
    occasionName,
    response: "Pending",
  }));
};

const formatGroupExport = (group) => {
  if (!group) return "";
  const lines = [String(group.kindLabel || "Communication").toUpperCase()];
  communicationBlocks(group).forEach((block) => {
    if (block.occasionName) {
      lines.push(`  Occasion: ${block.occasionName}`);
    }
    block.lines.forEach((item) => {
      const channel = item.channel || (item.channels || [])[0] || "Sent";
      const when = formatSendDateTime(item.sendDate) || "—";
      lines.push(`    Type: ${channel}`);
      lines.push(`    Sent: ${when}`);
    });
  });
  return lines.join("\n");
};

const formatCommunicationsExport = (row) => {
  const groups = row.inviteGroups || [];
  if (!groups.length) return "No invites sent";
  return groups.map(formatGroupExport).filter(Boolean).join("\n\n");
};

const GuestInviteReport = () => {
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [kindFilter, setKindFilter] = useState("all");

  const loadReport = useCallback(async () => {
    if (!selectedEvent?.eventId) {
      setReport(null);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(getGuestInviteReport(selectedEvent.eventId));
      setReport(res.data?.data || null);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load guest & invites report"
      );
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [selectedEvent?.eventId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const rows = useMemo(() => {
    const list = report?.rows || [];
    return list.filter((row) => {
      if (kindFilter !== "all" && !((row.kindCounts || {})[kindFilter] > 0)) {
        return false;
      }
      if (!searchTerm.trim()) return true;
      const q = searchTerm.toLowerCase();
      return [
        row.name,
        row.email,
        row.mobile,
        row.regNo,
        row.personType,
        ...(row.allocatedOccasions || []),
        ...(row.sentOccasions || []),
        ...(row.inviteTypes || []),
        ...(row.inviteKinds || []),
        ...(row.inviteGroups || []).flatMap((group) => [
          group.kindLabel,
          ...(group.items || []).map((item) =>
            formatGroupItem(item, group.kind, group.eventLevel)
          ),
        ]),
        ...(row.rsvpDetails || []).map(
          (item) => `${item.occasionName} ${item.response}`
        ),
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [report, searchTerm, kindFilter]);

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";

  const handleExport = () => {
    if (!rows.length) {
      toast.error("No rows to export.");
      return;
    }
    const eventLabel = safeFileLabel(selectedEvent?.eventName, "Event");
    exportRowsToExcel({
      rows,
      sheetName: "Guest Invites",
      fileName: `${eventLabel}_Guest_Invites.xlsx`,
      reportTitle: "Guest & Invites Report",
      eventName: selectedEvent?.eventName || "",
      columns: [
        { label: "Person Type", value: (r) => r.personType || "", width: 12 },
        {
          label: "Guest / Attendee Name",
          value: (r) => r.name || "",
          width: 24,
        },
        {
          label: "Registration No.",
          value: (r) => r.regNo || "",
          width: 16,
        },
        { label: "Email", value: (r) => r.email || "", width: 26 },
        { label: "Mobile", value: (r) => r.mobile || "", width: 14 },
        {
          label: "Allocated Occasions",
          value: (r) => (r.allocatedOccasions || []).join("\n"),
          width: 22,
          wrap: true,
        },
        {
          label: "Communications sent",
          value: (r) => formatCommunicationsExport(r),
          width: 46,
          wrap: true,
        },
        {
          label: "RSVP by occasion",
          value: (r) =>
            rsvpRowsForPerson(r)
              .map((item) => `${item.occasionName}: ${item.response}`)
              .join("\n"),
          width: 24,
          wrap: true,
        },
        {
          label: "Guest Itinerary Link",
          value: (r) =>
            r.itineraryPath ? `${origin}${r.itineraryPath}` : "",
          width: 48,
        },
      ],
    });
    toast.success("Export successful!");
  };

  const summary = report?.summary;
  const rsvpByOccasion = summary?.rsvpByOccasion || [];
  const invitesByOccasion = summary?.invitesByOccasion || [];

  return (
    <ReportPageShell
      title="Guest & Invites Report"
      subtitle="Who received each communication: Invites (by occasion), Accommodation, Travel, Seating, and Itinerary."
      hasEvent={Boolean(selectedEvent?.eventId)}
      loading={loading}
      onRefresh={loadReport}
      onExport={handleExport}
      exportDisabled={!report}
      metrics={[
        {
          label: "Guests",
          value: summary?.totalPeople ?? 0,
          hint: "Guests + attendees",
        },
        {
          label: "Accommodation sent",
          value: summary?.staySent ?? 0,
          hint: "Guests sent stay details",
          tone: "is-covered",
        },
        {
          label: "Travel sent",
          value: summary?.travelSent ?? 0,
          hint: "Guests sent travel details",
        },
        {
          label: "Seating sent",
          value: summary?.seatingSent ?? 0,
        },
        {
          label: "Itinerary sent",
          value: summary?.itinerarySent ?? 0,
        },
      ]}
      toolbar={
        <div className="stay-report-toolbar festiva-invite-toolbar">
          <input
            type="search"
            className="form-control stay-report-search festiva-invite-search"
            placeholder="Search name, reg no, email…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="form-select festiva-report-select festiva-invite-select"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            aria-label="Filter by invite type"
          >
            {KIND_FILTERS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      }
    >
      {invitesByOccasion.length || rsvpByOccasion.length ? (
        <section
          className="festiva-occasion-summary-row"
          aria-label="Invites and RSVP by occasion"
        >
          {invitesByOccasion.length ? (
            <article className="festiva-occasion-summary-panel">
              <div className="festiva-occasion-summary-head">
                <h3>Invites by occasion</h3>
                <span>
                  {invitesByOccasion.reduce(
                    (sum, occ) => sum + (occ.allocated || 0),
                    0
                  )}{" "}
                  allocated ·{" "}
                  {invitesByOccasion.reduce(
                    (sum, occ) => sum + (occ.invited || 0),
                    0
                  )}{" "}
                  invited
                </span>
              </div>
              <table className="festiva-occasion-summary-table">
                <thead>
                  <tr>
                    <th>Occasion</th>
                    <th>Allocated</th>
                    <th>Invited</th>
                    <th>Email</th>
                    <th>WhatsApp</th>
                  </tr>
                </thead>
                <tbody>
                  {invitesByOccasion.map((occ) => (
                    <tr key={`invite-${String(occ.occasionId)}`}>
                      <td>{occ.occasionName}</td>
                      <td>{occ.allocated ?? 0}</td>
                      <td>{occ.invited ?? 0}</td>
                      <td>{occ.email ?? 0}</td>
                      <td>{occ.whatsapp ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </article>
          ) : null}

          {rsvpByOccasion.length ? (
            <article className="festiva-occasion-summary-panel">
              <div className="festiva-occasion-summary-head">
                <h3>RSVP by occasion</h3>
                <span>
                  {rsvpByOccasion.reduce(
                    (sum, occ) => sum + (occ.allocated || 0),
                    0
                  )}{" "}
                  allocated · Yes {summary?.rsvpYesResponses ?? 0} · No{" "}
                  {summary?.rsvpNoResponses ?? 0} · Pending{" "}
                  {summary?.rsvpPendingResponses ?? 0}
                </span>
              </div>
              <table className="festiva-occasion-summary-table">
                <thead>
                  <tr>
                    <th>Occasion</th>
                    <th>Allocated</th>
                    <th className="is-yes">Yes</th>
                    <th className="is-no">No</th>
                    <th className="is-pending">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {rsvpByOccasion.map((occ) => (
                    <tr key={`rsvp-${String(occ.occasionId)}`}>
                      <td>{occ.occasionName}</td>
                      <td>{occ.allocated ?? 0}</td>
                      <td className="is-yes">{occ.yes ?? 0}</td>
                      <td className="is-no">{occ.no ?? 0}</td>
                      <td className="is-pending">{occ.pending ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </article>
          ) : null}
        </section>
      ) : null}

      <section className="stay-report-section">
        <div className="stay-report-table-wrap">
          <div className="stay-report-table-head">
            <h3>Guest & invite register</h3>
            <span>{rows.length} guests</span>
          </div>
          <div className="stay-report-table-scroll">
            <table className="table stay-report-table">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Reg No</th>
                  <th>Contact</th>
                  <th>Allocated occasions</th>
                  <th>Communications sent</th>
                  <th>RSVP by occasion</th>
                  <th>Itinerary</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? (
                  rows.map((row, index) => {
                    const rsvpRows = rsvpRowsForPerson(row);
                    return (
                    <tr key={`${row.guestId}-${row.attendeeId || "guest"}`}>
                      <td>{index + 1}</td>
                      <td>{row.personType}</td>
                      <td>
                        <strong>{row.name}</strong>
                      </td>
                      <td>{row.regNo || "—"}</td>
                      <td>
                        <div>{row.email || "—"}</div>
                        <div className="festiva-report-muted">{row.mobile || ""}</div>
                      </td>
                      <td>
                        {(row.allocatedOccasions || []).length ? (
                          <ul className="festiva-stack-list">
                            {(row.allocatedOccasions || []).map((name) => (
                              <li key={`${row.guestId}-${row.attendeeId || "g"}-${name}`}>
                                {name}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="festiva-report-muted">—</span>
                        )}
                      </td>
                      <td className="festiva-comm-cell">
                        <CommunicationsCell
                          rowKey={`${row.guestId}-${row.attendeeId || "g"}`}
                          groups={row.inviteGroups || []}
                        />
                      </td>
                      <td>
                        {rsvpRows.length ? (
                          <ul className="festiva-rsvp-list">
                            {rsvpRows.map((item) => (
                              <li
                                key={`${row.guestId}-${row.attendeeId || "g"}-${
                                  item.occasionId || item.occasionName
                                }`}
                              >
                                <span className="festiva-rsvp-occ">
                                  {item.occasionName}
                                </span>
                                <span
                                  className={`festiva-rsvp-pill festiva-rsvp-pill--${String(
                                    item.response || "pending"
                                  ).toLowerCase()}`}
                                >
                                  {item.response}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="festiva-report-muted">—</span>
                        )}
                      </td>
                      <td>
                        {row.itineraryPath ? (
                          <a
                            href={`${origin}${row.itineraryPath}`}
                            target="_blank"
                            rel="noreferrer"
                            className="festiva-report-link"
                          >
                            Open
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      {loading ? "Loading…" : "No guests found for this event."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <p className="festiva-report-footnote">
          Communications are grouped by type (Invites, Stay, Travel, Seating,
          Itinerary). Invites and RSVP are counted from allocated guests per
          occasion. Generated {formatDate(new Date().toISOString())}.
        </p>
      </section>
    </ReportPageShell>
  );
};

export default GuestInviteReport;
